import React from "react";

export default function TermsContent() {
  return (
    <div className="space-y-6 text-sm leading-6 md:text-base">
      <p>
        Welcome to SoloConnect, a platform dedicated to helping solo travelers
        meet and connect with others traveling along similar routes and dates.
        Please review the following Terms and Conditions carefully, as they
        govern your access to and use of our services, mobile application, and
        website (collectively, “SoloConnect” or the “Platform”). 
      </p>

      <section>
        <h2 className="font-semibold text-lg text-gray-900">
          1. Acceptance of Terms
        </h2>
        <p>
          By accessing or using SoloConnect in any manner, including downloading
          the app, creating an account, or using any part of the platform, you
          acknowledge that you have read, understood, and agreed to be bound by
          these Terms and Conditions (“Terms”), along with our Privacy Policy. 
        </p>
        <p>
          If you do not accept these Terms, you may not access or use
          SoloConnect. 
        </p>
      </section>

      <section>
        <h2 className="font-semibold text-lg text-gray-900">
          2. Nature of Our Service
        </h2>
        <p>
          SoloConnect is a third-party service provider, offering a platform
          that connects solo travelers. Users create travel plans, browse
          compatible itineraries, and initiate chats with other travelers after
          paying a small fee. 
        </p>
        <p>
          When registering, we capture a real-time image of the user to help
          ensure profiles reflect the actual person. However, SoloConnect does
          not verify the identity, character, background, or behavior of users. 
        </p>
        <p>
          We are not involved in or responsible for any offline meetings or
          interactions. By using this platform, you acknowledge and accept that
          SoloConnect cannot and does not guarantee the conduct or intentions of
          any user you choose to interact or meet with. 
        </p>
      </section>

      <section>
        <h2 className="font-semibold text-lg text-gray-900">3. Eligibility</h2>
        <ul className="list-disc list-inside ml-4">
          <p>You must be at least 18 years old to use SoloConnect. </p>
          <p>By registering, you affirm:  </p>
          <li>You are at least 18 years of age; </li>
          <li>
            You have the legal capacity to enter into a binding agreement; 
          </li>
          <li>
            The information provided during registration is accurate and
            truthful; 
          </li>
          <li>You will comply with these Terms and all applicable laws. </li>
          <p>
            We reserve the right to suspend or delete accounts found to be in
            violation of these conditions. 
          </p>
        </ul>
      </section>

      <section>
        <h2 className="font-semibold text-lg text-gray-900">
          4. User Obligations & Conduct
        </h2>
        <ul className="list-disc list-inside ml-4">
          <p>
            You agree to use SoloConnect for lawful purposes and in a
            respectful, honest, and non-exploitative manner. You will not: 
          </p>

          <li>Use the app to defraud, harass, or harm others; </li>
          <li>
            Post or share content that is threatening, abusive, defamatory,
            obscene, racist, or sexually explicit; 
          </li>
          <li>
            Attempt to gain unauthorized access to other users' data or our
            systems; 
          </li>
          <li>
            Use the app for solicitation, advertising, or commercial purposes
            not explicitly authorized; 
          </li>
          <li>
            Misrepresent your identity, age, travel plans, or intentions. 
          </li>
          <p>
            SoloConnect reserves the right to moderate or remove any content
            that violates these guidelines and to take appropriate action,
            including suspension or termination of offending accounts.  
          </p>
        </ul>
      </section>

      <h2 className="text-lg font-semibold text-gray-900">
        5. Registration & Pay-to-Chat Fee
      </h2>
      <p>
        To ensure a higher quality of interactions and discourage spam or fake
        profiles, SoloConnect requires users to pay a small one-time fee to
        unlock chat with another traveler.
      </p>
      <ul className="list-disc list-inside ml-4 mt-2">
        <li>
          The fee ranges from ₹29–₹249, or its equivalent in your local
          currency, depending on your region and any promotional offers.
        </li>
        <li>
          Payment is securely processed via third-party gateways (Stripe,
          Razorpay, Phonepay).
        </li>
        <li>
          Once paid, chat becomes available between the user and the selected
          traveler.
        </li>
      </ul>

      <h2 className="text-lg md:text-xl font-semibold text-black mb-4">Note:</h2>
      <ul className="list-[lower-roman] list-inside space-y-3 text-black text-sm md:text-base leading-relaxed ml-4">
        <li>
          All payments made on{" "}
          <span className="font-semibold text-black">SoloConnect</span> are
          non-refundable, except in cases of verified technical errors or
          unauthorized transactions.
        </li>
        <li>
          Refund requests must be submitted within{" "}
          <span className="font-semibold text-black">7 days</span> of the
          transaction via
          <a
            href="mailto:support@soloconnect.com"
            className="text-blue-600 underline hover:text-purple-700 ml-1"
          >
            support@soloconnect.com
          </a>
          , with relevant proof of issue.
        </li>
      </ul>

      {/* 6. Platform Limitations & Risk Acknowledgment */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900">
          6. Platform Limitations & Risk Acknowledgment
        </h2>
        <p>By using SoloConnect, you acknowledge and agree to the following:</p>
        <ul className="list-disc list-inside ml-4 mt-2">
          <li>We do not screen, endorse, or monitor every user;</li>
          <li>
            You are solely responsible for any communication, decisions, or
            meetings that result from use of the platform;
          </li>
          <li>
            You are encouraged to take all necessary safety precautions (meet in
            public, inform someone you trust, verify user identity
            independently);
          </li>
          <li>
            SoloConnect is not liable for damages, injuries, or disputes
            resulting from user interactions—online or offline.
          </li>
        </ul>
      </section>

      {/* 7. Account Suspension & Termination */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900">
          7. Account Suspension & Termination
        </h2>
        <p>
          SoloConnect reserves the right to suspend, restrict, or permanently
          terminate your access if:
        </p>
        <ul className="list-disc list-inside ml-4 mt-2">
          <li>You violate these Terms;</li>
          <li>
            Your conduct threatens the safety or experience of other users;
          </li>
          <li>
            You are reported multiple times or engage in prohibited behavior;
          </li>
          <li>Your payment fails or is charged back fraudulently.</li>
        </ul>
      </section>

      {/* 8. Intellectual Property */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900">
          8. Intellectual Property
        </h2>
        <p>
          All content, designs, logos, text, and visuals on SoloConnect are the
          exclusive property of the company and protected by applicable
          copyright and intellectual property laws. Users may not reproduce,
          repurpose, or distribute any app content without written permission.
        </p>
        <p className="mt-2">
          You retain ownership of content you upload (e.g., bios, travel notes),
          but by submitting it, you grant SoloConnect a non-exclusive,
          royalty-free, worldwide license to display it within the app for
          discovery purposes.
        </p>
      </section>

      <section>
        <h2 className="text-lg font-semibold text-gray-900">
          9. Limitation of Liability
        </h2>
        <p>
          To the maximum extent permitted by law, SoloConnect and its
          affiliates, officers, directors, and employees shall not be liable
          for:
        </p>
        <ul className="list-disc list-inside ml-4 mt-2">
          <li>Indirect, incidental, punitive, or consequential damages;</li>
          <li>Losses from meeting or communicating with other users;</li>
          <li>Technical issues, interruptions, or data breaches.</li>
        </ul>
        <p className="mt-2">
          Your sole remedy for dissatisfaction with the platform is to
          discontinue use.
        </p>
      </section>

      {/* 10. Changes to Terms */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900">
          10. Changes to Terms
        </h2>
        <p>
          SoloConnect may revise these Terms at any time. Users will be notified
          of major changes via email or in-app notification. Continued use after
          updates constitutes acceptance.
        </p>
      </section>

      {/* 11. Governing Law */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900">
          11. Governing Law
        </h2>
        <p>
          These Terms are governed by and construed under the laws of India. Any
          disputes shall be resolved in the courts of New Delhi, India.
        </p>
      </section>

      {/* 12. Contact Information */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900">
          12. Contact Information
        </h2>
        <p>
          For support or legal inquiries, contact:
          <span className="font-semibold"> support@soloconnect.in</span>
        </p>
      </section>

      <p>
        <strong>Effective Date:</strong> 10th July 2025
        <br />
        <strong>App Name:</strong> SoloConnect
        <br />
        <strong>Last Updated:</strong> 21st July 2025
      </p>
    </div>
  );
}
